/**
 * Générateur — Automatisme : puissance comme multiplication itérée :
 * 3 × 3 × 3 × 3 = 3⁴.
 * Attendu ID : 1217 | Niveau : 3ème | Domaine : Puissances
 * Rubrique : AUTOMATISME (programme 2025, cycle 4 page 11, ligne 743 :
 * « Puissance comme multiplication itérée : 3 × 3 × 3 × 3 = 3⁴. »).
 *
 * ── Périmètre (cadrage programme-maths 2026-09-12, mode auto) ─────────────
 * UNE ÉCRITURE dans les deux sens : produit répété → puissance (3 × 3 × 3 ×
 * 3 = 3⁴) et puissance → produit (2⁵ = 2 × 2 × 2 × 2 × 2). Jumeau de 1096
 * (« 3 + 3 + 3 + 3 = 4 × 3 » : rien à calculer, une écriture) et inverse
 * de 1103 (qui donnait la VALEUR de 2², 2³, 3³). Le neuf de 3e : les
 * exposants 4 à 6. La valeur n'est JAMAIS demandée ni affichée (choix mode
 * auto, comme 1096 et 1108 : écriture pure ; À ARBITRER).
 * Frontières : bases mêlées 2 × 2 × 3 × 3 × 3 (1221, l. 781), base décimale
 * ou négative (l. 640), produits de puissances (1218/1219).
 * Banque MathsMentales (S. Cogez, Apache 2.0, relevé 2026-09-12) : 6ND25
 * option 2 « Notation carré, cube » (n 1..5) s'arrête à l'exposant 3 ; les
 * bornes viennent du programme et de l'ancien attendu 3e 2019 (« 8 × 8 × 8
 * × 8 × 8 »).
 *
 * ── Plan de cartes ─────────────────────────────────────────────────────────
 *   Carte 1 « Série flash » (flash_texte_trou_saisie) : quatre lignes KaTeX,
 *     UN trou par ligne : l'exposant (« 3 × 3 × 3 × 3 = 3^⬚ », deux fois),
 *     la base avec l'exposant verrouillé (« 5 × 5 × 5 × 5 × 5 = ⬚⁵ »), et
 *     une ligne inverse (« 2⁵ = 2 × 2 × 2 × 2 × ⬚ » : le dernier facteur) ;
 *     une base 10 par série, quatre bases distinctes, un exposant ≥ 4.
 *   Carte 2 « Quelle écriture ? » (ecriture_qcm_unique) : sens direct
 *     « 3 × 3 × 3 × 3, c'est… » [3⁴ ; 4³ ; 4 × 3 ; 3⁵] ou sens inverse
 *     « 2⁵, c'est… » [2 × 2 × 2 × 2 × 2 ; 5 × 5 ; 2 × 5 ; 2 × 2 × 2 × 2],
 *     une fois sur deux ; contenu.sousType = direct / inverse.
 *   Carte 3 « Vrai ou faux » (sens_tableau_vf) : quatre lignes d'écriture,
 *     vraies ou fausses par une famille ; jamais une valeur.
 *
 * INVARIANTS : base 2..10 (1 exclu, 10 admis : le pont vers 10ⁿ) ;
 * exposant 2..6, exposant ≠ base (3 × 3 × 3 = 3³ ne tranche rien) ; au QCM,
 * le nombre de facteurs ≠ la base ; chaque écriture re-vérifiée (le produit
 * compte bien `exposant` facteurs égaux à `base`, throw) ; jamais 4 × 3 et
 * 12 ensemble (même valeur) : la valeur n'apparaît nulle part.
 * SACRIFIÉ (fiche d'aide « le petit 4 compte les 3 ») : (3 ; 4) dans les
 * deux sens, sur tout ce qui s'affiche (clé et distracteurs), auto-testé par
 * gen1217.self.test.js.
 *
 * ── Erreurs (familles nommées) ───────────────────────────────────────────
 *   exposant_base_inverses : 3 × 3 × 3 × 3 → 4³, PRINCIPALE (symétrique de
 *                            DEPP 2de 2025 q.2 : « multiplie la base par
 *                            l'exposant », 10,9 %) ;
 *   multiplication_iteree  : → 4 × 3, la clé de 1096 appliquée à un produit ;
 *   compte_faux            : → 3⁵, 3³ (un facteur de trop ou de moins) ;
 *   base_fois_exposant     : 5⁴ = 5 × 4 (au Vrai/Faux, en écriture).
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeTexteTrou, tex,
} from '../../../utils';

const ATTENDU_ID = 1217;
const opts = (label) => ({ niveau: '3eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_FLASH = 'Série flash';
const L_QCM = 'Quelle écriture ?';
const L_VF = 'Vrai ou faux';

// ─── Le domaine ───────────────────────────────────────────────────────────

/** L'exemple du programme et de la fiche, sacrifié dans les deux sens. */
export const SACRIFIE = { base: 3, exp: 4 };
const estSacrifie = (base, exp) => base === SACRIFIE.base && exp === SACRIFIE.exp;

/** Un couple (base, exposant) : base 2..10, exposant 2..6, exposant ≠ base, hors sacrifié ; `expMin` impose un exposant minimal. */
const tirer = ({ expMin = 2, baseForce = null, exclure = [] } = {}) => {
    for (let essai = 0; essai < 100; essai++) {
        const base = baseForce ?? randInt(2, 10);
        const exp = randInt(expMin, 6);
        if (exp === base || estSacrifie(base, exp) || exclure.includes(base)) continue;
        return { base, exp };
    }
    return { base: 2, exp: 5 };
};

// ─── Écritures (KaTeX) ────────────────────────────────────────────────────

const puissTex = (base, exp) => `${base}^{${exp}}`;
const produitTex = (base, exp) => Array(exp).fill(String(base)).join(' \\times ');
/** Assert : le produit compte bien `exp` facteurs `base`. */
const verifier = (base, exp) => {
    const facteurs = produitTex(base, exp).split(' \\times ');
    if (facteurs.length !== exp || facteurs.some((f) => f !== String(base))) throw new Error(`gen_1217 : écriture ${base}^${exp}`);
    return true;
};

const EXPLICATIONS = {
    exposant_base_inverses: 'Le nombre répété est la base, en bas ; le petit nombre en haut compte combien de fois : 3 × 3 × 3 × 3, c\'est quatre fois 3, donc 3 puissance 4.',
    multiplication_iteree: '4 × 3, c\'est 3 + 3 + 3 + 3 (une addition répétée). Une multiplication répétée s\'écrit avec un exposant : 3 puissance 4.',
    compte_faux: 'Je compte les facteurs un par un : l\'exposant, c\'est exactement leur nombre.',
    base_fois_exposant: '5 puissance 4, c\'est 5 × 5 × 5 × 5, pas 5 × 4.',
    regle: 'Un produit de facteurs tous égaux s\'écrit en puissance : la base est le facteur, l\'exposant compte les facteurs.',
};

// ─── Carte 1 : Série flash ────────────────────────────────────────────────

const CONSIGNE_FLASH = '<p><b>Complète.</b></p>';

/** Une ligne : l'exposant à trouver, la base à trouver (exposant verrouillé), ou le dernier facteur du produit. */
const ligneFlash = (type, { base, exp }) => {
    verifier(base, exp);
    if (type === 'exposant') return `$${produitTex(base, exp)} = ${base}^{ {{${exp}}} }$`;
    if (type === 'base') return `$${produitTex(base, exp)} = {{${base}}}^{${exp}}$`;
    return `$${puissTex(base, exp)} = ${produitTex(base, exp - 1)} \\times {{${base}}}$`;
};

const genFlash = () => {
    for (let essai = 0; essai < 100; essai++) {
        const types = shuffle(['exposant', 'exposant', 'base', 'facteur']);
        const items = [];
        const dix = randInt(0, 3);
        for (let i = 0; i < 4; i++) {
            items.push(tirer({ expMin: i === 0 ? 4 : 2, baseForce: i === dix ? 10 : null, exclure: items.map((it) => it.base) }));
        }
        if (new Set(items.map((it) => it.base)).size < 4 || !items.some((it) => it.base === 10)) continue;
        const phrase = items.map((it, i) => ligneFlash(types[i], it)).join('\n');
        const payload = makeTexteTrou(phrase, CONSIGNE_FLASH, { ...opts(L_FLASH), mode: 'saisie', strict: true });
        payload.contenu = { ...payload.contenu, explications: { regle: EXPLICATIONS.regle, exposant_base_inverses: EXPLICATIONS.exposant_base_inverses, compte_faux: EXPLICATIONS.compte_faux } };
        payload._meta.params = { items, types };
        return payload;
    }
    const items = [{ base: 2, exp: 5 }, { base: 10, exp: 4 }, { base: 7, exp: 3 }, { base: 5, exp: 2 }];
    const types = ['exposant', 'exposant', 'base', 'facteur'];
    const payload = makeTexteTrou(items.map((it, i) => ligneFlash(types[i], it)).join('\n'), CONSIGNE_FLASH, { ...opts(L_FLASH), mode: 'saisie', strict: true });
    payload.contenu = { ...payload.contenu, explications: { regle: EXPLICATIONS.regle } };
    payload._meta.params = { items, types };
    return payload;
};

// ─── Carte 2 : Quelle écriture ? (QCM) ────────────────────────────────────

/** Les distracteurs du sens direct (« 3 × 3 × 3 × 3, c'est… »), chacun une écriture en puissance ou en produit, jamais la clé ni le sacrifié. */
const distracteursDirect = ({ base, exp }) => {
    const c = [
        { code: 'exposant_base_inverses', texte: puissTex(exp, base) },
        { code: 'multiplication_iteree', texte: `${exp} \\times ${base}` },
        { code: 'compte_faux', texte: puissTex(base, randomChoice(exp === 2 ? [3] : [exp - 1, exp + 1])) },
    ];
    return c.filter((x) => x.texte !== puissTex(base, exp) && x.texte !== puissTex(SACRIFIE.base, SACRIFIE.exp));
};
/** Les distracteurs du sens inverse (« 2⁵, c'est… »), en produits. */
const distracteursInverse = ({ base, exp }) => {
    const c = [
        { code: 'exposant_base_inverses', texte: produitTex(exp, base) },
        { code: 'multiplication_iteree', texte: `${base} \\times ${exp}` },
        { code: 'compte_faux', texte: produitTex(base, randomChoice(exp === 2 ? [3] : [exp - 1, exp + 1])) },
    ];
    return c.filter((x) => x.texte !== produitTex(base, exp) && x.texte !== produitTex(SACRIFIE.base, SACRIFIE.exp));
};

const genQcm = () => {
    for (let essai = 0; essai < 100; essai++) {
        const it = tirer({ expMin: 3 });
        // sens inverse seulement pour une base ≤ 6 : le produit inversé (base facteurs) reste lisible
        const sens = it.base > 6 ? 'direct' : randomChoice(['direct', 'inverse']);
        if (it.exp === it.base || it.exp - 1 === it.base || it.exp + 1 === it.base) continue; // les produits de compte_faux et de l'inversion restent discernables
        const faux = sens === 'direct' ? distracteursDirect(it) : distracteursInverse(it);
        if (faux.length < 3) continue;
        const bon = tex(sens === 'direct' ? puissTex(it.base, it.exp) : produitTex(it.base, it.exp));
        const choix = [bon, ...faux.map((f) => tex(f.texte))];
        if (new Set(choix).size !== 4) continue;
        const stem = sens === 'direct' ? `<p><b>${tex(produitTex(it.base, it.exp))}, c'est…</b></p>` : `<p><b>${tex(puissTex(it.base, it.exp))}, c'est…</b></p>`;
        const payload = makeQcm(choix, bon, stem, opts(L_QCM));
        payload.contenu = { ...payload.contenu, sousType: sens, explications: EXPLICATIONS };
        payload._meta.params = { ...it, sens, distracteurs: faux.map(({ code }) => ({ code })) };
        return payload;
    }
    // Repli vérifié : 2 × 2 × 2 × 2 × 2, c'est 2⁵ ; 5², 5 × 2, 2⁴.
    const bon = tex(puissTex(2, 5));
    const payload = makeQcm([bon, tex(puissTex(5, 2)), tex('5 \\times 2'), tex(puissTex(2, 4))], bon, `<p><b>${tex(produitTex(2, 5))}, c'est…</b></p>`, opts(L_QCM));
    payload.contenu = { ...payload.contenu, sousType: 'direct', explications: EXPLICATIONS };
    payload._meta.params = { base: 2, exp: 5, sens: 'direct', distracteurs: [{ code: 'exposant_base_inverses' }, { code: 'multiplication_iteree' }, { code: 'compte_faux' }] };
    return payload;
};

// ─── Carte 3 : Vrai ou faux ───────────────────────────────────────────────

/** Une ligne « produit = puissance » (ou l'inverse) vraie, ou fausse par une famille tirée. */
const ligneVf = (it, vraie) => {
    const { base, exp } = it;
    verifier(base, exp);
    const inverse = randomChoice([true, false]);
    const egalite = (g, d) => tex(inverse ? `${d} = ${g}` : `${g} = ${d}`);
    if (vraie) return { ligne: egalite(produitTex(base, exp), puissTex(base, exp)), code: null };
    const familles = [
        { code: 'exposant_base_inverses', ligne: egalite(produitTex(exp, base), puissTex(base, exp)) },
        { code: 'compte_faux', ligne: egalite(produitTex(base, randomChoice(exp === 2 ? [3] : [exp - 1, exp + 1])), puissTex(base, exp)) },
        { code: 'base_fois_exposant', ligne: egalite(`${base} \\times ${exp}`, puissTex(base, exp)) },
    ].filter((f) => !f.ligne.includes(produitTex(SACRIFIE.base, SACRIFIE.exp)) && !f.ligne.includes(puissTex(SACRIFIE.base, SACRIFIE.exp)));
    if (!familles.length) return null;
    return randomChoice(familles);
};

const genVf = () => {
    for (let essai = 0; essai < 100; essai++) {
        const items = [];
        for (let i = 0; i < 4; i++) items.push(tirer({ expMin: i === 0 ? 4 : 2, exclure: items.map((it) => it.base) }));
        if (new Set(items.map((it) => it.base)).size < 4) continue;
        const nbVraies = randomChoice([1, 2, 2, 3]);
        const verites = shuffle([...Array(nbVraies).fill(true), ...Array(4 - nbVraies).fill(false)]);
        const lignes = items.map((it, i) => ligneVf(it, verites[i]));
        if (lignes.some((l) => !l) || new Set(lignes.map((l) => l.ligne)).size < 4) continue;
        const payload = makeVraiFaux(lignes.map((l) => l.ligne), verites.map((v) => (v ? 0 : 1)), '<p><b>Vrai ou faux ?</b></p>', opts(L_VF));
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
        payload._meta.params = { items, verites, distracteurs: lignes.filter((l) => l.code).map(({ code }) => ({ code })) };
        return payload;
    }
    // Repli vérifié : 2 × 2 × 2 × 2 × 2 = 2⁵ V ; 5 × 5 × 5 × 5 = 4⁵ F ; 7³ = 7 × 3 F ; 10 × 10 × 10 × 10 = 10⁴ V.
    const lignes = [tex(`${produitTex(2, 5)} = ${puissTex(2, 5)}`), tex(`${produitTex(5, 4)} = ${puissTex(4, 5)}`), tex(`${puissTex(7, 3)} = 7 \\times 3`), tex(`${produitTex(10, 4)} = ${puissTex(10, 4)}`)];
    const payload = makeVraiFaux(lignes, [0, 1, 1, 0], '<p><b>Vrai ou faux ?</b></p>', opts(L_VF));
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    payload._meta.params = { items: [{ base: 2, exp: 5 }, { base: 5, exp: 4 }, { base: 7, exp: 3 }, { base: 10, exp: 4 }], verites: [true, false, false, true], distracteurs: [{ code: 'exposant_base_inverses' }, { code: 'base_fois_exposant' }] };
    return payload;
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'quatre choix : la clé et trois familles ; des ÉCRITURES seulement, jamais une valeur (32 pour 2⁵ n\'apparaît nulle part)',
        'exposant ≠ base, et au QCM |exposant − base| ≥ 2 : l\'inversion et le compte faux restent discernables',
        'sacrifié : 3⁴ et 3 × 3 × 3 × 3, ni en clé ni en distracteur',
    ],
    qcm_unique: {
        support: 'base 2..10, exposant 3..6 ; sens direct (produit → puissance) ou inverse (puissance → produit) une fois sur deux, contenu.sousType',
        familles: {
            exposant_base_inverses: '4³ pour 3 × 3 × 3 × 3 (5 × 5 pour 2⁵), la principale',
            multiplication_iteree: '4 × 3 (2 × 5) : la règle de 1096 appliquée à un produit',
            compte_faux: '3⁵ ou 3³ : un facteur de trop ou de moins',
        },
    },
    vf_tableau_vf: 'quatre lignes, quatre bases distinctes, un exposant ≥ 4 ; les fausses tirent exposant_base_inverses, compte_faux ou base_fois_exposant',
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    flash_texte_trou_saisie: genFlash,
    ecriture_qcm_unique: genQcm,
    sens_tableau_vf: genVf,
};

// etapes : nombre d'étapes de raisonnement de la carte, validé par le prof le
// 2026-10-03 (chantier groupes-differencies) ; sert aux exercices complexes du groupe Fort.
export const CATEGORIES = [
    { id: 'flash', label: L_FLASH, etapes: 1, formatsList: [{ key: 'flash_texte_trou_saisie', label: 'Saisie trous' }] },
    { id: 'ecriture', label: L_QCM, etapes: 1, formatsList: [{ key: 'ecriture_qcm_unique', label: 'QCM' }] },
    { id: 'sens', label: L_VF, etapes: 1, formatsList: [{ key: 'sens_tableau_vf', label: 'Vrai/Faux' }] },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
